import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// --- PWA cache-busting & iframe guard ---
const PWA_ICONS_VERSION = "v5";

const isInIframe = (() => {
  try { return window.self !== window.top; } catch { return true; }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

if (isPreviewHost || isInIframe) {
  // Never let a SW run inside Lovable preview / iframes
  navigator.serviceWorker?.getRegistrations().then((regs) => {
    regs.forEach((r) => r.unregister());
  });
} else if ("serviceWorker" in navigator) {
  // In production: force SW update + purge old icon caches once per icon version
  const versionKey = "pwa-icons-version";
  const stored = localStorage.getItem(versionKey);

  if (stored !== PWA_ICONS_VERSION) {
    navigator.serviceWorker.getRegistrations().then(async (regs) => {
      // Force each SW to check for an update
      for (const reg of regs) {
        try { await reg.update(); } catch { /* ignore */ }
      }
      // Delete all workbox/precache caches so stale icons are purged
      const names = await caches.keys();
      await Promise.all(
        names
          .filter((n) => n.startsWith("workbox-precache") || n.includes("assets"))
          .map((n) => caches.delete(n))
      );
      localStorage.setItem(versionKey, PWA_ICONS_VERSION);
    });
  }
}
// --- end PWA guard ---

// --- Stale chunk recovery: after a new deploy, old chunk files disappear.
// Reload once to fetch the fresh build instead of showing a blank screen.
const RELOAD_KEY = "chunk-reload-at";
const reloadOnce = () => {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  if (Date.now() - last > 10_000) {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }
};
window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault();
  reloadOnce();
});
window.addEventListener("unhandledrejection", (e) => {
  const msg = String((e.reason && (e.reason as Error).message) || e.reason || "");
  if (/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg)) {
    reloadOnce();
  }
});

createRoot(document.getElementById("root")!).render(<App />);
